import { Component, ElementRef, EventEmitter, Input, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { NgbModal, NgbModalOptions, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { ReportCategoryResponse, ReportTreeNodeResponse } from '../../../../../core/components/reports/models/responses';
import { first } from 'rxjs/operators';
import { PostReportCategoryRequest } from '../../../../../core/components/reports/models/requests';
import { ReportsService } from '../../../../../core/components/reports/reports.service';
import { ToastrService } from 'ngx-toastr';
import { DivisionResponse } from '../../../../../core/services/division.service';

@Component({
  selector: 'app-report-category-name-modal',
  templateUrl: './report-category-name-modal.component.html',
  styleUrls: ['./report-category-name-modal.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class ReportCategoryNameModalComponent {
  @ViewChild('categoryNameModal') categoryNameModalElement: ElementRef;
  private categoryNameModal: NgbModalRef;
  
  @Output() saveCategory: EventEmitter<ReportCategoryResponse> = new EventEmitter<ReportCategoryResponse>();
  @Input() categoryList: ReportTreeNodeResponse[] = [];
  @Input() reportTree: ReportTreeNodeResponse[] = [];
  @Input() divisionList: DivisionResponse[] = [];
  
  public categoryName = '';
  public title = '';
  public divisionId: number | null = null;
  public reportCategoryId: number | null = null;
  public parentCategoryId: number | null = null;
  public parentNode: ReportTreeNodeResponse | null = null;
  public currentDivisionId: number | null = null;

  public isSaving = false;

  constructor(
    private modalService: NgbModal,
    private reportsService: ReportsService,
    private toastrService: ToastrService,
  ) { }

  private findNodeById(nodes: ReportTreeNodeResponse[], id: number): ReportTreeNodeResponse | null {
    if (!nodes || nodes.length === 0) {
      return null;
    }

    for (const node of nodes) {
      if (node.id === id) {
        return node;
      }
      if (node.children && node.children.length > 0) {
        const found = this.findNodeById(node.children, id);
        if (found) {
          return found;
        }
      }
    }

    return null;
  }

  private hasDescendant(node: ReportTreeNodeResponse, searchId: number): boolean {
    if (!node || !node.children || node.children.length === 0) {
      return false;
    }

    for (const child of node.children) {
      if (child.id === searchId) {
        return true;
      }

      if (this.hasDescendant(child, searchId)) {
        return true;
      }
    }

    return false;
  }

  public openEdit(node: ReportTreeNodeResponse, parentNode: ReportTreeNodeResponse): void {
    this.title = 'Edit Category';
    this.reportCategoryId = node.id;
    this.parentNode = parentNode || null;
    this.parentCategoryId = parentNode ? parentNode.id : null;
    this.currentDivisionId = (node as any).divisionId != null ? (node as any).divisionId as number : null;
    this.categoryName = node.name;
    this.divisionId = parentNode && (parentNode as any).divisionId != null ? (parentNode as any).divisionId as number : 
      node && (node as any).divisionId != null ? (node as any).divisionId as number : null;
    const modalConfig: NgbModalOptions = {
      container: '.app',
      size: <any>'md',
      backdrop: 'static',
      keyboard: false,
    };

    this.categoryNameModal = this.modalService.open(this.categoryNameModalElement, modalConfig);
  }
  
  public openAdd(parentNode: ReportTreeNodeResponse): void {
    this.title = 'New Category';
    this.reportCategoryId = 0;
    this.parentNode = parentNode || null;
    this.parentCategoryId = parentNode ? parentNode.id : null;
    this.currentDivisionId = parentNode && (parentNode as any).divisionId != null ? (parentNode as any).divisionId as number : null;
    this.categoryName = '';
    this.divisionId = parentNode && (parentNode as any).divisionId != null ? (parentNode as any).divisionId as number : null;
    const modalConfig: NgbModalOptions = {
      container: '.app',
      size: <any>'md',
      backdrop: 'static',
      keyboard: false,
    };

    this.categoryNameModal = this.modalService.open(this.categoryNameModalElement, modalConfig);
  }

  public close(): void {
    if (this.categoryNameModal) {
      this.categoryNameModal.close();
    }
  }

  onSave(): void {
    this.isSaving = true;
    if (!this.categoryName || this.categoryName.trim().length === 0) {
      this.toastrService.error('Category name cannot be empty.', 'Invalid Category Name');
      this.isSaving = false;
      return;
    }
    if(!this.divisionId){
      this.toastrService.error('Please select a division for the category.', 'Division Required');
      this.isSaving = false;
      return;
    }

    // Prevent circular dependencies: a category cannot become a child of itself
    // or of any of its own descendants.
    if (this.reportCategoryId && this.reportCategoryId !== 0 && this.parentCategoryId != null) {
      const currentNode = this.findNodeById(this.reportTree, this.reportCategoryId);
      if (currentNode && (this.parentCategoryId === this.reportCategoryId || this.hasDescendant(currentNode, this.parentCategoryId))) {
        this.toastrService.error('A category cannot be a child of itself or its descendants.', 'Invalid Category Parent');
        this.isSaving = false;
        return;
      }
    }

    const request: PostReportCategoryRequest = {
      id: this.reportCategoryId || 0,
      name: this.categoryName.trim(),
      parentCategoryId: this.parentCategoryId || undefined,
      divisionId: this.divisionId
    };
    this.reportsService.setReportCategory(request)
    .pipe(first())
    .subscribe((category: ReportCategoryResponse) => {
      this.close();
      this.toastrService.success('Report category saved successfully.', 'Success');
      this.saveCategory.emit(category);
      this.isSaving = false;
    },(err)=> {
      console.error('Error saving report category:', err);
      this.toastrService.error('Failed to save report category. Please try again.', 'Error');
      this.isSaving = false;
    });   
  }

  public onParentCategoryChange(): void {
    if (this.parentCategoryId != null) {
      const parentNode = this.findNodeById(this.reportTree, this.parentCategoryId);
      if (parentNode && (parentNode as any).divisionId != null) {
        this.divisionId = (parentNode as any).divisionId as number;
      } else {
        this.divisionId = null;
      }
    } else {
      this.divisionId = null;
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/developer/components/developer-tools/report-type-editor/report-category-name-modal/report-category-name-modal.component.ts